import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {DoppeActionTypeServiceViewerLang} from './doppe-action-type-service-viewer.lang';
import {arrayFirst, arrayRemoveNullValues, asArray, evaluateFunction, immerProduce, stringIsNotNullableOrWhiteSpace, RichTextContent, HideableValue, hideableValueCreateHidden, hideableValueGetValue, hideableValueIsVisibleAndNotEqualValue, hideableValueModifyValue, OverridableValue, overridableValueCreateOriginal, overridableValueGetOverriddenValue, overridableValueGetValue, overridableValueIsOriginal } from '@wix/devzai-utils-common';
import type {
    BymoPageProps,
    BymoPagePropsContentSettingsMetadata,
    DoppeActionPriceData,
    DoppeDtoBookingService
} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity,
    doppeDtoBookingServiceConvertToFormattedPrice,
    doppeDtoBookingServiceLocationConvertToActionFormattedLocation,
    doppeDtoBookingServiceScheduleConvertToActionFormattedDuration,
    doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule,
    DoppeExternalServiceProvider,
    doppeExternalServiceProviderResolveBusinessEntityReference,
    DoppeLinkSpecEntityType,
    doppeLinkSpecCreateEntity
} from '../../client-server-common';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {BymoPageResource} from '../../client/resources/bymo-page-resource/bymo-page-resource';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';

export namespace DoppeActionTypeService {

    export type ServiceSettings = {
        servicePageLink: string;
        serviceTitle: HideableValue<OverridableValue<string>>;
        description: HideableValue<OverridableValue<RichTextContent>>;
        serviceImages: OverridableValue<WixMediaResource[] | WixMediaResource | null>;
        priceData: HideableValue<OverridableValue<DoppeActionPriceData>>;
        servicePeriod: HideableValue<OverridableValue<string>>;
        serviceSchedule: HideableValue<OverridableValue<string>>;
        serviceParticipants: HideableValue<OverridableValue<string>>;
        location: HideableValue<OverridableValue<string>>;
        couponCode: HideableValue<string>;
        ribbon: HideableValue<OverridableValue<string>>;
        serviceProvider: DoppeExternalServiceProvider | null;
        serviceProviderId: string | null;
    }

    export type ActionSettings = DoppeActionTypeService.ServiceSettings &
        DoppeActionContentSectionsSettings &
        DoppeActionCtaButtonsSettings & {

    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        serviceButtonText?: HideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeServiceActionContentSettingsMetadata = {
    serviceTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
    servicePageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    serviceImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    servicePeriod: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDuration),
    serviceSchedule: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityScheduleInfo),
    serviceParticipants: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityParticipantsInfo),
    location: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityAddress),
    priceData: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PriceData, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeService.ActionSettings>

export const doppeActionTypeService = doppeActionTypeDefineMetadata<DoppeActionTypeService.ActionSettings, DoppeActionTypeService.PartialProps>({
    id: 'io.bymo.action/service',
    resolveActionSettings: action => {

        const {
            servicePageLink = '',
            serviceButtonText = '',
            ctaButton,
            ctaButtons,
            sections,
            priceData,
            ...restSettings
        } = action.settings;

        return {
            servicePageLink: servicePageLink,
            priceData: hideableValueModifyValue(priceData ?? {actualPrice: DoppeActionTypeServiceViewerLang.defaultTexts.price, discountPrice: ''}, overridableValue => {
                const innerValue = overridableValueGetValue(overridableValue, {actualPrice: '', discountPrice: ''});
                const resolved = {...innerValue, actualPrice: innerValue.actualPrice ?? '', discountPrice: innerValue.discountPrice ?? ''};
                return overridableValueIsOriginal(overridableValue)
                    ? overridableValueCreateOriginal(resolved)
                    : resolved;
            }),
            serviceTitle: DoppeActionTypeServiceViewerLang.defaultTexts.serviceTitle,
            description: DoppeActionTypeServiceViewerLang.defaultTexts.description,
            serviceButtonText: serviceButtonText,
            serviceImages: null,
            location: '',
            servicePeriod: '',
            serviceSchedule: hideableValueCreateHidden(''),
            serviceParticipants: hideableValueCreateHidden(''),
            couponCode: hideableValueCreateHidden(''),
            ribbon: hideableValueCreateHidden(''),
            serviceProvider: null,
            serviceProviderId: null,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: serviceButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, true),
            ...restSettings
        }
    },
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeServiceActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BookNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                location: true,
                priceData: true,
                ribbon: true,
                serviceParticipants: true,
                servicePeriod: true,
                serviceSchedule: true,
                serviceTitle: true
            },
        bindableContentSettingsNames: {
                description: true,
                location: true,
                priceData: true,
                ribbon: true,
                serviceImages: true,
                serviceParticipants: true,
                servicePeriod: true,
                serviceSchedule: true,
                serviceTitle: true
            }
    },
    actionListItemSettingsMetadata: false,
    resolveUsedMedia: actionSettings => {
        const serviceImages = overridableValueGetValue(actionSettings.serviceImages);
        return arrayRemoveNullValues(asArray(serviceImages ?? null));
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const boundService = actionSettings.serviceProvider && actionSettings.serviceProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalServiceProviderResolveBusinessEntityReference(actionSettings.serviceProvider!, actionSettings.serviceProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBookingService : null;
        })() : null;

        const serviceImages = overridableValueGetOverriddenValue(
            actionSettings.serviceImages,
            boundService?.media ?? null
        );
        return serviceImages ? asArray(serviceImages)[0] ?? null : null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            const serviceImages = overridableValueGetValue(actionSettings.serviceImages);
            if (serviceImages) {
                const images = asArray(serviceImages);
                actionSettings.serviceImages = images.map(image => {
                    return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                })
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.servicePageLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const sourceLinkSpec = evaluateFunction(() => {
            if (actionSettings.serviceProvider === DoppeExternalServiceProvider.Hopp && actionSettings.serviceProviderId !== null) {
                return doppeLinkSpecCreateEntity(DoppeLinkSpecEntityType.Service, actionSettings.serviceProviderId);
            }
            return stringIsNotNullableOrWhiteSpace(actionSettings.servicePageLink) ? actionSettings.servicePageLink : null;
        });

        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: sourceLinkSpec,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: (actionSettings, bymoPageContextProps) => {
        const boundService = actionSettings.serviceProvider && actionSettings.serviceProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalServiceProviderResolveBusinessEntityReference(actionSettings.serviceProvider!, actionSettings.serviceProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBookingService : null;
        })() : null;

        const description = hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, boundService?.description ?? '')
        );
        const serviceTitle = hideableValueModifyValue(
            actionSettings.serviceTitle,
            value => overridableValueGetOverriddenValue(value, boundService?.name ?? '')
        );
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: hideableValueGetValue(actionSettings.couponCode),
            couponDescription: hideableValueIsVisibleAndNotEqualValue(description, '') ? description : '',
            couponTitle: hideableValueIsVisibleAndNotEqualValue(serviceTitle, '') ? serviceTitle : '',
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.serviceProvider !== null && actionSettings.serviceProviderId !== null ?
                doppeExternalServiceProviderResolveBusinessEntityReference(
                    actionSettings.serviceProvider,
                    actionSettings.serviceProviderId
                ) :
                null
        ])
    },
    resolveActionEntityBinding: actionSettings => {
        const {serviceProvider, serviceProviderId} = actionSettings;
        return serviceProvider !== null && serviceProviderId !== null
            ? doppeExternalServiceProviderResolveBusinessEntityReference(serviceProvider, serviceProviderId)
            : null;
    },
    resolveSettingsFromBoundEntityReference: (ref) => {
        switch (ref.businessEntity) {
            case DoppeBusinessEntity.WixService:
                return {serviceProvider: DoppeExternalServiceProvider.Wix, serviceProviderId: ref.id};
            case DoppeBusinessEntity.HoppService:
                return {serviceProvider: DoppeExternalServiceProvider.Hopp, serviceProviderId: ref.id};
            default:
                return {};
        }
    },
    getSupportedBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.LinkedMetaSite]: {
            [DoppeSupportedWixTpas.Booking]: [DoppeBusinessEntity.WixService],
        },
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.Booking]: [DoppeBusinessEntity.HoppService],
        },
    }),
    resolveActualActionSettings: (actionSettings, bymoPageContextProps, bymoPageProps) => {
        const {serviceProvider, serviceProviderId} = actionSettings;
        if (serviceProvider !== null && serviceProviderId !== null) {
            const boundService = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalServiceProviderResolveBusinessEntityReference(serviceProvider, serviceProviderId)
            );
            if (boundService && !doppeBusinessEntityDtoIsNotFound(boundService)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeServiceResolveSettingsFromBoundEntity(actionSettings, boundService as DoppeDtoBookingService, bymoPageProps),
                };
            }
        }
        return actionSettings;
    },
});

export function doppeActionTypeServiceResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeService.ServiceSettings,
    doppeDtoService: DoppeDtoBookingService,
    bymoPageProps: BymoPageProps,
): DoppeActionTypeService.ServiceSettings {

    const entityPriceData: DoppeActionPriceData = doppeDtoService.payment
        ? {actualPrice: doppeDtoBookingServiceConvertToFormattedPrice(doppeDtoService.payment, ''), discountPrice: ''}
        : {actualPrice: '', discountPrice: ''};

    const entityLocation = arrayFirst(doppeDtoService.locations);

    const durations = doppeDtoService.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedDuration({
        schedule: doppeDtoService.scheduale,
        serviceType: doppeDtoService.type
    }) : null;

    const schedule = doppeDtoService.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule({
        schedule: doppeDtoService.scheduale,
        serviceType: doppeDtoService.type,
        bymoPageProps: bymoPageProps
    }) : null;

    return {
        ...actionSettings,
        priceData: hideableValueModifyValue(
            actionSettings.priceData,
            value => overridableValueGetOverriddenValue(value, entityPriceData)
        ),
        serviceTitle: hideableValueModifyValue(
            actionSettings.serviceTitle,
            value => overridableValueGetOverriddenValue(value, doppeDtoService.name ?? '')
        ),
        description: hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, doppeDtoService.description ?? '')
        ),
        serviceImages: overridableValueGetOverriddenValue(
            actionSettings.serviceImages,
            doppeDtoService.media
        ),
        servicePeriod: hideableValueModifyValue(
            actionSettings.servicePeriod,
            value => overridableValueGetOverriddenValue(value, durations?.[0] ?? '')
        ),
        serviceSchedule: hideableValueModifyValue(
            actionSettings.serviceSchedule,
            value => overridableValueGetOverriddenValue(value, schedule ?? '')
        ),
        serviceParticipants: hideableValueModifyValue(
            actionSettings.serviceParticipants,
            value => overridableValueGetOverriddenValue(value, doppeDtoService.capacity ? doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity(doppeDtoService.capacity) : '')
        ),
        location: hideableValueModifyValue(
            actionSettings.location,
            value => overridableValueGetOverriddenValue(value, entityLocation ? doppeDtoBookingServiceLocationConvertToActionFormattedLocation(entityLocation) : '')
        ),
        ribbon: hideableValueModifyValue(
            actionSettings.ribbon,
            value => overridableValueGetOverriddenValue(value, '')
        ),
        servicePageLink: stringIsNotNullableOrWhiteSpace(doppeDtoService.servicePageUrl) ?
            doppeDtoService.servicePageUrl : 'about:blank',
    }
}

export function populateServiceActionSettingsDefaultTexts(options: {
    service: DoppeActionTypeService.ActionSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}) : DoppeActionTypeService.ActionSettings {

    const {
        service,
        pageContentLocales,
    } = options;

    const defaultButtonText = pageContentLocales["service.actionSettings.buttonText"]

    return  {
        ...service,
        serviceTitle: hideableValueModifyValue(service.serviceTitle, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.title"];
        }),
        description: hideableValueModifyValue(service.description, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.description"];
        }),
        couponCode: hideableValueModifyValue(service.couponCode, (value) => {
            return value !== '' ? value : pageContentLocales["service.actionSettings.couponCode"];
        }),
        ribbon: hideableValueModifyValue(service.ribbon, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.ribbon"];
        }),
        location: hideableValueModifyValue(service.location, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.location"];
        }),
        serviceParticipants: hideableValueModifyValue(service.serviceParticipants, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.serviceParticipants"];
        }),
        serviceSchedule: hideableValueModifyValue(service.serviceSchedule, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.serviceSchedule"];
        }),
        servicePeriod: hideableValueModifyValue(service.servicePeriod, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.servicePeriod"];
        }),
        priceData: hideableValueModifyValue(service.priceData, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            if (value === undefined || value.actualPrice !== '') {
                return overridableValue;
            }
            return {actualPrice: pageContentLocales["service.actionSettings.price"], discountPrice: ''};
        }),
        sections: [],
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: defaultButtonText,
                isRequired: false,
                settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
            })],
        }, false),
    }
}


export function convertDoppeWixBookingServiceToServiceSettings(service: DoppeDtoBookingService, bymoPageResource?: BymoPageResource, settings?: DoppeActionTypeService.ActionSettings, serviceProvider?: DoppeExternalServiceProvider) {

    const durations = service.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedDuration({
        schedule: service.scheduale,
        serviceType: service.type
    }) : null;

    const schedule = service.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule({
        schedule: service.scheduale,
        serviceType: service.type,
        bymoPageProps: bymoPageResource ? bymoPageResource.getBymoPageProps() : undefined
    }) : null;

    return {
        ctaButtons: [],
        sections: [],
        couponCode: hideableValueCreateHidden(''),
        ribbon: hideableValueCreateHidden(overridableValueCreateOriginal('')),
        ...settings,
        serviceImages: overridableValueCreateOriginal(service.media),
        servicePageLink: service.servicePageUrl ?? settings?.servicePageLink ?? ``,
        priceData: service.payment
            ? overridableValueCreateOriginal({actualPrice: doppeDtoBookingServiceConvertToFormattedPrice(service.payment, ''), discountPrice: ''})
            : hideableValueCreateHidden(overridableValueCreateOriginal({actualPrice: '', discountPrice: ''})),
        serviceParticipants: service.capacity
            ? overridableValueCreateOriginal(doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity(service.capacity))
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        serviceSchedule: schedule
            ? overridableValueCreateOriginal(schedule)
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        location: service.locations[0]
            ? overridableValueCreateOriginal(service.locations.map(location => doppeDtoBookingServiceLocationConvertToActionFormattedLocation(location)).toString())
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        servicePeriod: durations
            ? overridableValueCreateOriginal(durations[0])
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        serviceTitle: overridableValueCreateOriginal(service?.name ?? ''),
        description: service.description
            ? overridableValueCreateOriginal(service.description)
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        serviceProvider: serviceProvider ?? DoppeExternalServiceProvider.Wix,
        serviceProviderId: service.id
    }
}